import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { ActivitySpeciesTypeDTO, ActivityTypeDTO, ActivityCategoryDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useActivityTypes } from '../../../hooks/useActivityTypeApi';
import { useActivityCategories } from '../../../hooks/useActivityCategoryApi';
import { useActivitySpeciesTypeApi } from '../../../hooks/useActivitySpeciesTypeApi';
import ActivitySpeciesTypeTable from '../../../components/tables/ActivitySpeciesTypeTable';

const ActivitySpeciesTypeList: React.FC = () => {
  const { search, remove, isLoading, error } = useActivitySpeciesTypeApi();
  const [items, setItems] = useState<ActivitySpeciesTypeDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [selectedTypeId, setSelectedTypeId] = useState<string | undefined>(undefined);

  const { data: activityTypes = [] } = useActivityTypes();
  const { data: activityCategories = [], isLoading: isLoadingActivityCategories } = useActivityCategories();
  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Activity Species', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Activity Species:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!selectedTypeId) {
        setActivityCategories([]);
        return;
      }
      try {
        const response = await activityCategoryApi.search({
          filters: {
            activityTypeId: selectedTypeId,
            isActive: true,
          },
        });
        if (response?.data) {
          setActivityCategories(response.data.results ?? []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [selectedTypeId]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const filters = {
      ...searchFilters,
      // Only include IDs if they have a non-empty value
      activityTypeId: selectedTypeId || undefined,
      activityCategoryId: searchFilters.activityCategoryId || undefined,
      // Remove empty strings from other filters
      ...Object.entries(searchFilters).reduce((acc, [key, value]) => {
        if (key !== 'activityTypeId' && key !== 'activityCategoryId' && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>)
    };
    
    setCurrentPage(1);
    fetchItems(1, pageSize, filters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    setSelectedTypeId(undefined);
    setActivityCategories([]);
    fetchItems(1, pageSize, {});
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const newTypeId = e.target.value;
    setSelectedTypeId(newTypeId);
    setSearchFilters((prev: any) => ({
      ...prev,
      activityTypeId: newTypeId,
      activityCategoryId: '',
    }));
  };

  const handleDelete = async (dto: ActivitySpeciesTypeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting ActivitySpeciesType:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/activityspeciestype"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.ActivitySpeciesType.View"
    >
      <Card
        title="Manage Activity Species"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/activityspeciestype/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="activityTypeId"
              label="Search by Activity Type"
              type="dropdown"
              value={selectedTypeId}
              onChange={handleTypeChange}
              placeholder="All Types"
              options={activityTypes.map((type) => ({
                value: type.id,
                label: type.title,
              }))}
            />
            <FormField
              id="activityCategoryId"
              label="Search by Categories"
              type="dropdown"
              value={searchFilters.activityCategoryId || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, activityCategoryId: e.target.value })
              }
              placeholder="All Categories"
              options={activityCategories.map((category) => ({
                value: category.id,
                label: category.title,
              }))}
              disabled={!selectedTypeId}
            />
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <ActivitySpeciesTypeTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/activityspeciestype/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default ActivitySpeciesTypeList;